import { useAuthStore } from '@sentinel/web-core';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { SPACES, type SpaceKey } from './navigation';
import { spaceOfPage, visibleSpaces, type VisibleSpace } from './spaces';

const STORAGE_KEY = 'sentinel.shell.space';

/** The last space used in this browser, like «Questa postazione». Storage may be refused
 *  (private window, blocked site data): the PCA is the default then. */
function stored(): SpaceKey {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'secretariat' ? 'secretariat' : 'pca';
  } catch {
    return 'pca';
  }
}

interface SpaceState {
  remembered: SpaceKey;
  remember: (space: SpaceKey) => void;
}

const useSpaceStore = create<SpaceState>((set) => ({
  remembered: stored(),
  remember: (space) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, space);
    } catch {
      // Not remembered in this browser: the choice holds until the page is reloaded.
    }
    set({ remembered: space });
  },
}));

export interface CurrentSpace {
  spaces: VisibleSpace[];
  current: VisibleSpace | null;
  /** Opens a space: stays on the page when it belongs there, else goes to its first entry. */
  choose: (space: SpaceKey) => void;
}

/** The spaces one may use and the one of the open page (2.2 of the PCA/Segreteria proposal). */
export function useCurrentSpace(): CurrentSpace {
  const permissions = useAuthStore((s) => s.me?.permissions);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const remembered = useSpaceStore((s) => s.remembered);
  const remember = useSpaceStore((s) => s.remember);
  const navigate = useNavigate();
  const spaces = useMemo(() => visibleSpaces(SPACES, permissions ?? []), [permissions]);
  const key = spaceOfPage(pathname, spaces, remembered);
  const current = spaces.find((s) => s.key === key) ?? null;

  // A page of one space only (a link, a reload) moves the remembered space with it, so the next
  // page shared by both stays in the space one is working in.
  useEffect(() => {
    if (key !== null && key !== remembered) remember(key);
  }, [key, remembered, remember]);

  const choose = (space: SpaceKey) => {
    const target = spaces.find((s) => s.key === space);
    if (target === undefined) return;
    remember(space);
    // Stay when the open page belongs to the chosen space at least as well as to the other.
    if (spaceOfPage(pathname, spaces, space) === space) return;
    const first = target.entries[0];
    if (first !== undefined) void navigate({ to: first.to });
  };

  return { spaces, current, choose };
}
